import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSpinner,
    faCheckCircle,
    faExclamationTriangle,
    faInfoCircle,
    faClock,
    faRefresh,
    faTimes,
    faSearch,
    faDownload,
    faEye,
    faBolt,
    faDatabase,
    faRocket,
    faServer,
    faChevronLeft,
    faChevronRight,
    faAngleDoubleLeft,
    faAngleDoubleRight
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import apiHelper from '../../utils/apiHelper';
import './QdrantLogsViewer.css';

const QdrantLogsViewer = ({ isOpen, onClose, autoRefreshOnOpen = false }) => {
    const [logs, setLogs] = useState([]);
    const [summary, setSummary] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(100);
    const [isLoading, setIsLoading] = useState(false);
    const [lastRefresh, setLastRefresh] = useState(null);
    const [error, setError] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(autoRefreshOnOpen); // Start with auto-refresh if requested
    const [refreshInterval, setRefreshInterval] = useState(5000); // 5 seconds
    
    const [selectedStatuses, setSelectedStatuses] = useState(() => new Set());

    const toggleStatusFilter = (status) => {
        const next = new Set(selectedStatuses);
        if (next.has(status)) next.delete(status);
        else next.add(status);
        setSelectedStatuses(next);
        setCurrentPage(1);
    };
    
    // Refs for auto-refresh
    const intervalRef = useRef(null);
    const logsContainerRef = useRef(null);
    const [shouldScrollToTop, setShouldScrollToTop] = useState(false);

    // Load logs when component opens
    useEffect(() => {
        if (isOpen) {
            loadLogs();
        }
    }, [isOpen]);

    // Auto-refresh functionality
    useEffect(() => {
        if (autoRefresh && isOpen) {
            intervalRef.current = setInterval(() => {
                loadLogs(true); // Silent refresh
            }, refreshInterval);
        } else {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [autoRefresh, refreshInterval, isOpen]);

    // Auto-scroll to top when new logs arrive (since newest are at top)
    useEffect(() => {
        if (shouldScrollToTop && logsContainerRef.current) {
            logsContainerRef.current.scrollTop = 0;
            setShouldScrollToTop(false);
        }
    }, [logs, shouldScrollToTop]);

    const loadLogs = async (silent = false) => {
        if (!silent) {
            setIsLoading(true);
            setError(null);
        }

        try {
            const response = await apiHelper.get(`/api/graph/qdrant-logs`);
            
            if (response.data.success) {
                const wasAtTop = !silent && logsContainerRef.current && logsContainerRef.current.scrollTop === 0;
                
                setLogs(response.data.logs);
                setSummary(response.data.summary);
                setLastRefresh(new Date());
                setCurrentPage(1); // Reset to first page when data changes
                
                // Only scroll to top on initial load or if user was already at top
                if (!silent || wasAtTop) {
                    setShouldScrollToTop(true);
                }
            } else {
                setError(response.data.error || 'Failed to load logs');
            }
        } catch (error) {
            console.error('Error loading logs:', error);
            setError(error.message || 'Failed to load logs');
            
            if (!silent) {
                toast.error('Failed to load logs');
            }
        } finally {
            if (!silent) {
                setIsLoading(false);
            }
        }
    };

    const getStatusIcon = (status) => {
        switch ((status || '').toUpperCase()) {
            case 'STARTED':
                return { icon: faRocket, color: '#007bff', bgColor: '#e7f3ff' };
            case 'IN_PROGRESS':
                return { icon: faBolt, color: '#ffc107', bgColor: '#fff8e1' };
            case 'SUCCESS':
            case 'COMPLETED':
                return { icon: faCheckCircle, color: '#28a745', bgColor: '#e8f5e9' };
            case 'PARTIAL':
                return { icon: faExclamationTriangle, color: '#fd7e14', bgColor: '#fff3e0' };
            case 'FAILED':
            case 'ERROR':
                return { icon: faExclamationTriangle, color: '#dc3545', bgColor: '#fdebee' };
            case 'DOCUMENT_FAILED':
                return { icon: faExclamationTriangle, color: '#b71c1c', bgColor: '#ffebee' };
            case 'DOCUMENT_SKIPPED':
                return { icon: faInfoCircle, color: '#6c757d', bgColor: '#f8f9fa' };
            default:
                return { icon: faInfoCircle, color: '#6c757d', bgColor: '#f8f9fa' };
        }
    };

    const getOperationIcon = (operation) => {
        const op = (operation || '').toUpperCase();
        if (op === 'CREATE' || op === 'PROMOTE') return { icon: faRocket, color: '#007bff' };
        if (op === 'INDEX' || op.startsWith('INDEX_')) return { icon: faRefresh, color: '#007bff' };
        if (op === 'CLEANUP') return { icon: faServer, color: '#6c757d' };
        if (op === 'TEST_COMPLETED') return { icon: faSearch, color: '#28a745' };
        if (op.startsWith('REPOSITORY')) return { icon: faDatabase, color: '#17a2b8' };
        return { icon: faServer, color: '#6c757d' };
    };

    //   "Section: news | Stage: processing_article | DocID: 123 | Title: ... | Error: ..."
    const parseFailureDetails = (details) => {
        if (!details) return null;
        const parts = {};
        details.split('|').forEach((segment) => {
            const idx = segment.indexOf(':');
            if (idx > -1) {
                const key = segment.slice(0, idx).trim().toLowerCase();
                const value = segment.slice(idx + 1).trim();
                parts[key] = value;
            }
        });
        return parts;
    };

    const FAILURE_STATUSES = ['FAILED', 'ERROR', 'DOCUMENT_FAILED'];
    const isFailure = (log) =>
        FAILURE_STATUSES.includes((log.status || '').toUpperCase());

    const filteredLogs = selectedStatuses.size === 0
        ? logs
        : logs.filter((log) => selectedStatuses.has((log.status || '').toUpperCase()));

    // Client-side pagination
    const totalPages = Math.ceil(filteredLogs.length / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const currentLogs = filteredLogs.slice(startIndex, endIndex);

    const goToPage = (page) => {
        setCurrentPage(page);
    };

    const nextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const previousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const changePageSize = (newPageSize) => {
        setPageSize(newPageSize);
        setCurrentPage(1);
    };



    const exportLogs = () => {
        const exportData = filteredLogs.map(log => ({
            timestamp: log.formattedDate,
            operation: log.operation,
            status: log.status,
            title: log.title,
            collection: log.collectionName,
            details: log.details,
            createdBy: log.createdBy
        }));

        const csvContent = [
            ['Timestamp', 'Operation', 'Status', 'Title', 'Collection', 'Details', 'Created By'],
            ...exportData.map(row => [
                row.timestamp,
                row.operation,
                row.status,
                row.title,
                row.collection,
                row.details,
                row.createdBy
            ])
        ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `qdrant-logs-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (!isOpen) return null;

    return (
        <div className="qdrant-logs-modal-overlay">
            <div className="qdrant-logs-modal">
                <div className="logs-header">
                    <div className="header-left">
                        <h2>
                            <FontAwesomeIcon icon={faEye} />
                            &nbsp;Qdrant Processing Logs
                        </h2>
                        <div className="summary-stats">
                            <span className="">
                                📊 Total: {summary.total || 0}
                            </span>
                            <span className="">
                                🔥 Recent: {summary.recentActivity || 0}
                            </span>
                            {lastRefresh && (
                                <span className="">
                                    🕐 Updated: {lastRefresh.toLocaleTimeString()}
                                </span>
                            )}
                        </div>
                    </div>
                    
                    <div className="header-actions">
                        <button
                            className="action-btn refresh-btn"
                            onClick={() => loadLogs()}
                            disabled={isLoading}
                            title="Refresh logs"
                        >
                            <FontAwesomeIcon icon={isLoading ? faSpinner : faRefresh} spin={isLoading} />
                        </button>

                        <button
                            className={`action-btn auto-refresh-btn ${autoRefresh ? 'active' : ''}`}
                            onClick={() => setAutoRefresh(!autoRefresh)}
                            title={autoRefresh ? 'Disable auto-refresh' : 'Enable auto-refresh'}
                        >
                            <FontAwesomeIcon icon={faClock} />
                        </button>
                        
                        <button
                            className="action-btn export-btn"
                            onClick={exportLogs}
                            title="Export logs"
                        >
                            <FontAwesomeIcon icon={faDownload} />
                        </button>
                        
                        <button 
                            className="action-btn"
                            onClick={onClose}
                            title="Close"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                </div>

                {/* Auto-refresh controls */}
                {autoRefresh && (
                    <div className="auto-refresh-controls">
                        <span>🔄 Auto-refresh every:</span>
                        <select 
                            value={refreshInterval} 
                            onChange={(e) => setRefreshInterval(parseInt(e.target.value))}
                        >
                            <option value={2000}>2 seconds</option>
                            <option value={5000}>5 seconds</option>
                            <option value={10000}>10 seconds</option>
                            <option value={30000}>30 seconds</option>
                        </select>
                    </div>
                )}

                {summary.statusCounts && Object.keys(summary.statusCounts).length > 0 && (
                    <div className="status-summary">
                        {selectedStatuses.size > 0 && (
                            <button
                                onClick={() => { setSelectedStatuses(new Set()); setCurrentPage(1); }}
                                style={{
                                    border: '1px dashed #6c757d',
                                    background: '#fff',
                                    color: '#6c757d',
                                    padding: '4px 10px',
                                    borderRadius: 16,
                                    fontSize: '0.85rem',
                                    cursor: 'pointer',
                                }}
                                title="Clear status filter"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                &nbsp;Clear filter ({selectedStatuses.size})
                            </button>
                        )}
                        {Object.entries(summary.statusCounts).map(([status, count]) => {
                            const statusInfo = getStatusIcon(status);
                            const normalized = (status || '').toUpperCase();
                            const isActive = selectedStatuses.has(normalized);
                            const anySelected = selectedStatuses.size > 0;

                            return (
                                <button
                                    key={status}
                                    className="status-count-item"
                                    onClick={() => toggleStatusFilter(normalized)}
                                    style={{
                                        cursor: 'pointer',
                                        border: isActive
                                            ? `2px solid ${statusInfo.color}`
                                            : '1px solid #e0e0e0',
                                        backgroundColor: isActive
                                            ? statusInfo.bgColor
                                            : '#fff',
                                        opacity: anySelected && !isActive ? 0.55 : 1,
                                        padding: '4px 10px',
                                        borderRadius: 16,
                                        fontWeight: isActive ? 600 : 400,
                                        transition: 'all 0.15s ease',
                                    }}
                                    title={isActive
                                        ? `Click to remove ${status} from filter`
                                        : `Click to filter logs by ${status}`}
                                >
                                    <FontAwesomeIcon
                                        icon={statusInfo.icon}
                                        style={{ color: statusInfo.color }}
                                    />
                                    &nbsp;<span>{status}: {count}</span>
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Client-side Pagination Controls */}
                {totalPages > 1 && (
                    <div className="pagination-section">
                        <div className="pagination-info">
                            <span>
                                Showing {startIndex + 1}-{Math.min(endIndex, filteredLogs.length)} of {filteredLogs.length} logs
                                (Page {currentPage} of {totalPages})
                            </span>
                        </div>
                        
                        <div className="pagination-controls">
                            <button 
                                onClick={() => goToPage(1)}
                                disabled={currentPage === 1}
                                className="pagination-btn"
                                title="First Page"
                            >
                                <FontAwesomeIcon icon={faAngleDoubleLeft} />
                                First
                            </button>
                            
                            <button 
                                onClick={previousPage}
                                disabled={currentPage === 1}
                                className="pagination-btn"
                                title="Previous Page"
                            >
                                <FontAwesomeIcon icon={faChevronLeft} />
                                Previous
                            </button>
                            
                            <div className="page-info">
                                <span>Page {currentPage}</span>
                            </div>
                            
                            <button 
                                onClick={nextPage}
                                disabled={currentPage === totalPages}
                                className="pagination-btn"
                                title="Next Page"
                            >
                                Next
                                <FontAwesomeIcon icon={faChevronRight} />
                            </button>
                        </div>
                    </div>
                )}

                {/* Logs content */}
                <div className="logs-content" ref={logsContainerRef}>
                    {isLoading && logs.length === 0 ? (
                        <div className="loading-state">
                            <FontAwesomeIcon icon={faSpinner} spin />
                            <span>Loading logs...</span>
                        </div>
                    ) : error ? (
                        <div className="error-state">
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                            <span>{error}</span>
                            <button onClick={() => loadLogs()}>Try Again</button>
                        </div>
                    ) : filteredLogs.length === 0 ? (
                        <div className="empty-state">
                            <FontAwesomeIcon icon={faInfoCircle} />
                            <span>No logs found</span>
                        </div>
                    ) : (
                        <div className="logs-list">
                            {currentLogs.map((log, index) => {
                                const statusInfo = getStatusIcon(log.status);
                                const operationInfo = getOperationIcon(log.operation);
                                const statusUpper = (log.status || '').toUpperCase();
                                const isDocFailure = statusUpper === 'DOCUMENT_FAILED';
                                const isDocDegraded = statusUpper === 'DOCUMENT_DEGRADED';
                                // Both failure AND degraded entries use the pipe-delimited Details
                                // payload, so parse them both — the only difference is colour/label.
                                const failureFields = (isDocFailure || isDocDegraded)
                                    ? parseFailureDetails(log.details)
                                    : null;

                                return (
                                    <div
                                        key={`${log.id}-${index}`}
                                        className="log-item"
                                        style={
                                            isDocFailure
                                                ? { borderLeft: '4px solid #dc3545', backgroundColor: '#fffafa' }
                                                : isDocDegraded
                                                    ? { borderLeft: '4px solid #fd7e14', backgroundColor: '#fffaf0' }
                                                    : isFailure(log)
                                                        ? { borderLeft: '4px solid #fd7e14' }
                                                        : {}
                                        }
                                    >
                                        <div className="log-header">
                                            <div className="log-left">
                                                <div
                                                    className="status-badge"
                                                    style={{
                                                        backgroundColor: statusInfo.bgColor,
                                                        color: statusInfo.color
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={statusInfo.icon} />
                                                    {log.status}
                                                </div>

                                                <div className="operation-badge">
                                                    <FontAwesomeIcon
                                                        icon={operationInfo.icon}
                                                        style={{ color: operationInfo.color }}
                                                    />
                                                    {log.operation}
                                                </div>

                                                {/* Stage/section pill on per-doc failures */}
                                                {failureFields && failureFields.section && (
                                                    <div
                                                        className="stage-badge"
                                                        style={{
                                                            backgroundColor: '#fff3e0',
                                                            color: '#b71c1c',
                                                            padding: '2px 8px',
                                                            borderRadius: 4,
                                                            fontSize: '0.8rem',
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {failureFields.section}
                                                        {failureFields.stage ? ` → ${failureFields.stage}` : ''}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="log-right">
                                                <span className="log-time">{log.timeAgo}</span>
                                                <span className="log-date">{log.formattedDate}</span>
                                            </div>
                                        </div>

                                        <div className="log-content">
                                            <h4>{log.title}</h4>
                                            {log.collectionName && (
                                                <div className="collection-info">
                                                    <FontAwesomeIcon icon={faDatabase} />
                                                    Collection: {log.collectionName}
                                                </div>
                                            )}

                                            {failureFields ? (
                                                <div className="failure-details" style={{ marginTop: 8 }}>
                                                    {failureFields.docid && (
                                                        <div style={{ fontSize: '0.85rem', color: '#666' }}>
                                                            <strong>Doc ID:</strong> {failureFields.docid}
                                                        </div>
                                                    )}
                                                    {failureFields.url && (
                                                        <div style={{ fontSize: '0.85rem', marginTop: 4 }}>
                                                            <strong>Document:</strong>{' '}
                                                            <a
                                                                href={failureFields.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{
                                                                    color: '#0067b8',
                                                                    textDecoration: 'underline',
                                                                    wordBreak: 'break-all',
                                                                }}
                                                            >
                                                                Open in SharePoint ↗
                                                            </a>
                                                        </div>
                                                    )}
                                                    {(failureFields.error || failureFields.warning) && (
                                                        <div
                                                            style={{
                                                                marginTop: 6,
                                                                padding: '8px 12px',
                                                                // Red for hard failures, orange for degraded
                                                                backgroundColor: isDocDegraded ? '#fff3e0' : '#fdebee',
                                                                borderRadius: 4,
                                                                fontFamily: 'Monaco, monospace',
                                                                fontSize: '0.85rem',
                                                                color: isDocDegraded ? '#8a4b00' : '#721c24',
                                                                wordBreak: 'break-word',
                                                            }}
                                                        >
                                                            <strong>{isDocDegraded ? 'Warning:' : 'Error:'}</strong>{' '}
                                                            {failureFields.error || failureFields.warning}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <p className="log-details">{log.details}</p>
                                            )}

                                            {log.createdBy && (
                                                <div className="created-by">
                                                    👤 {log.createdBy}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Auto-refresh indicator */}
                {autoRefresh && (
                    <div className="auto-refresh-indicator">
                        <FontAwesomeIcon icon={faSpinner} spin />
                        Auto-refreshing every {refreshInterval / 1000}s
                    </div>
                )}
            </div>
        </div>
    );
};

export default QdrantLogsViewer; 